import ky from 'ky';
import delay from 'delay';
import {
  DefaultResourceOptions,
  DefaultRequestReturn,
  DefaultRequestOptions,
  createRequesterFn,
  defaultOptionsHandler as baseOptionsHandler,
} from '@gitbeaker/requester-utils';

export function defaultOptionsHandler(
  serviceOptions: DefaultResourceOptions,
  options: DefaultRequestOptions = {},
): DefaultRequestReturn {
  const opts: DefaultRequestReturn = baseOptionsHandler(serviceOptions, options);

  return { ...opts, headers: new Headers(opts.headers as Record<string, string>) };
}

export async function processBody(response: Response) {
  // Split to remove potential charset info from the content type
  const contentType = (response.headers.get('content-type') || '').split(';')[0].trim();

  if (contentType === 'application/json') {
    return response.json().then((v) => v || {});
  }

  if (contentType.startsWith('text/')) {
    return response.text().then((t) => t || '');
  }

  return response.arrayBuffer();
}

export async function handler(endpoint: string, options: Record<string, unknown>) {
  const retryCodes = [429, 502];
  const maxRetries = 10;

  for (let i = 0; i < maxRetries; i += 1) {
    try {
      const response = ky(endpoint, options);

      if (options.method === 'stream') return response;

      const resolved = await response; // eslint-disable-line
      const { status, headers: rawHeaders } = resolved;
      const headers = Object.fromEntries(rawHeaders.entries());
      const body = await processBody(resolved); // eslint-disable-line

      return { body, headers, status };
    } catch (e) {
      const waitTime = 2 ** i * 0.1;

      if (e.response) {
        if (retryCodes.includes(e.response.status)) {
          await delay(waitTime); // eslint-disable-line
          continue; // eslint-disable-line
        }

        try {
          const output = await e.response.json(); // eslint-disable-line
          e.description = output.error || output.message;
        } catch (err) {
          e.description = await e.response.text(); // eslint-disable-line
        }
      }

      throw e;
    }
  }

  throw new Error('Could not successfully complete this request');
}

export const requesterFn = createRequesterFn(defaultOptionsHandler, handler);
